import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-regular-svg-icons";

/**
 * DropzoneUpload
 * A small, dependency-free drag & drop file upload React component.
 *
 * Props:
 * - uploadUrl (string | null) : If provided the component will upload files to this endpoint via POST (FormData, field name: "file").
 * - multiple (boolean) : allow multiple files. Default: true
 * - accept (string) : mime types or extensions (same as input.accept). Default: "*\/*"
 * - maxSize (number) : max file size in bytes (per-file). Default: Infinity
 * - onUploadStart(files) : callback when upload starts
 * - onUploadProgress(file, percent) : progress updates (0-100)
 * - onUploadSuccess(file, response) : upload succeeded (response text)
 * - onUploadError(file, error) : upload failed
 * - onFilesChange(files) : called when file list changes (array of File objects)
 * - autoUpload (boolean) : automatically upload after drop/select if uploadUrl provided. Default: true
 *
 * Usage:
 * <DropzoneUpload uploadUrl="/api/upload" multiple accept="image/*" maxSize={5_000_000} />
 */

export default function DropzoneUpload({
  multiple = false,
  accept = "*/*",
  maxSize = Infinity,
  onFilesChange,
  files,
  setFiles,
  acceptText,
  square = false,
  title,
  subTitle,
  hideFileTypes = false,
  type = "file",
  darkBackground = false,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    return () => {
      files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    };
  }, [files]);

  const renderFileIcon = () => {
    switch (type) {
      case "image":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none">
            <path d="M0 14C0 6.26801 6.26801 0 14 0H42C49.732 0 56 6.26801 56 14V42C56 49.732 49.732 56 42 56H14C6.26801 56 0 49.732 0 42V14Z" fill="#E5E7EB"/>
            <path d="M36.1667 17.5H19.8333C18.5447 17.5 17.5 18.5447 17.5 19.8333V36.1667C17.5 37.4553 18.5447 38.5 19.8333 38.5H36.1667C37.4553 38.5 38.5 37.4553 38.5 36.1667V19.8333C38.5 18.5447 37.4553 17.5 36.1667 17.5Z" stroke="#99A1AF" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M24.4993 26.8346C25.788 26.8346 26.8327 25.79 26.8327 24.5013C26.8327 23.2126 25.788 22.168 24.4993 22.168C23.2107 22.168 22.166 23.2126 22.166 24.5013C22.166 25.79 23.2107 26.8346 24.4993 26.8346Z" stroke="#99A1AF" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M38.5 31.4983L34.8997 27.898C34.4621 27.4606 33.8687 27.2148 33.25 27.2148C32.6313 27.2148 32.0379 27.4606 31.6003 27.898L21 38.4983" stroke="#99A1AF" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        );
      case "audio":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z" fill="#F3F4F6"/>
            <path d="M24 15.668C23.337 15.668 22.7011 15.9314 22.2322 16.4002C21.7634 16.869 21.5 17.5049 21.5 18.168V24.0013C21.5 24.6643 21.7634 25.3002 22.2322 25.7691C22.7011 26.2379 23.337 26.5013 24 26.5013C24.663 26.5013 25.2989 26.2379 25.7678 25.7691C26.2366 25.3002 26.5 24.6643 26.5 24.0013V18.168C26.5 17.5049 26.2366 16.869 25.7678 16.4002C25.2989 15.9314 24.663 15.668 24 15.668Z" stroke="#99A1AF" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M29.8327 22.332V23.9987C29.8327 25.5458 29.2181 27.0295 28.1241 28.1235C27.0302 29.2174 25.5464 29.832 23.9993 29.832C22.4523 29.832 20.9685 29.2174 19.8746 28.1235C18.7806 27.0295 18.166 25.5458 18.166 23.9987V22.332" stroke="#99A1AF" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M24 29.832V32.332" stroke="#99A1AF" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        )
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z" fill="#F3F4F6"/>
            <path d="M31.5 26.5V29.8333C31.5 30.2754 31.3244 30.6993 31.0118 31.0118C30.6993 31.3244 30.2754 31.5 29.8333 31.5H18.1667C17.7246 31.5 17.3007 31.3244 16.9882 31.0118C16.6756 30.6993 16.5 30.2754 16.5 29.8333V26.5" stroke="#99A1AF" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M28.1673 20.6667L24.0007 16.5L19.834 20.6667" stroke="#99A1AF" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M24 16.5V26.5" stroke="#99A1AF" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        );
    } 
  }

  function handleFilesSelected(selectedFiles) {
    const arr = Array.from(selectedFiles);
    const normalized = arr.map((file, i) => {
      return {
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
        file,
        previewUrl: file.type.startsWith("image/") || file.type.startsWith("audio/") || file.type.startsWith("video/") ? URL.createObjectURL(file) : null,
        progress: 0,
        status: "ready",
        error: null,
      };
    }).filter(item => {
      if (item.file.size > maxSize) {
        item.status = "error";
        item.error = `File is larger than ${Math.round(maxSize / 1024)}KB`;
        return true;
      }
      return true;
    });

    setFiles(prev => {
      const next = multiple ? [...prev, ...normalized] : normalized;
      onFilesChange && onFilesChange(next.map(x => x.file));
      return next;
    });
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
      handleFilesSelected(e.dataTransfer.files);
    }
  }

  function openFileDialog() {
    inputRef.current && inputRef.current.click();
  }

  function onInputChange(e) {
    if (!e.target.files) return;
    handleFilesSelected(e.target.files);
    e.target.value = null; // reset so selecting same file again triggers change
  }

  return (
    <div className="w-100 max-w-xl mx-auto">
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') openFileDialog(); }}
        className={`file-drop-container flex f-center a-center border-2 border-dashed p-60 cursor-pointer transition-shadow focus:outline-none ${isDragging ? 'shadow-lg' : ''} ${square ? 'aspect-square' : ''}`}
        style={{ borderColor: isDragging ? 'rgba(59,130,246,0.6)' : '', backgroundColor: darkBackground ? '#F9FAFB' : '' }}
        onClick={openFileDialog}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          aria-label="Add File"
          multiple={multiple}
          accept={accept}
          onChange={onInputChange}
        />

        <div className="flex f-col a-center text-center gap-5">
          {/*<svg xmlns="http://www.w3.org/2000/svg" style={{width: 48, height: 48}} className="mb-10" viewBox="0 0 25 25" fill="none" stroke="currentColor">
            <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 104 4h6" />
            <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M7 16V8a4 4 0 014-4h1" />
            <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M16 16l-4-4-4 4" />
          </svg>*/}
          {renderFileIcon()}
          <div className="text-sm font-bold mt-10" style={{color: '#364153'}}>{title || 'Drag & Drop files here'}</div>
          <div className="text-xs" style={{color: '#99A1AF'}}>{subTitle || 'or click to browse files'}</div>
          {!hideFileTypes && (
            <div className="text-xs mt-2" style={{color: '#686868'}}>{(accept !== '*/*' ? `Supports: ${acceptText}` : 'Any file type allowed')}</div>
          )}
        </div>
      </div>
    </div>
  );
}